import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { emailService, EmailItem } from '../services/api';
import './EmailList.css';

const EmailList: React.FC = () => {
  const [emails, setEmails] = useState<EmailItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchSender, setSearchSender] = useState('');
  const [searchSubject, setSearchSubject] = useState('');
  const [limit, setLimit] = useState(50);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  // Detect mobile view and auto-collapse filter
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsFilterExpanded(false);
      } else {
        setIsFilterExpanded(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchEmails = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { limit };
      if (searchSender) params.sender = searchSender;
      if (searchSubject) params.subject = searchSubject;
      
      const data = await emailService.getEmails(params);
      setEmails(data.items);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch emails');
      console.error('Error fetching emails:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmails();
  }, [limit]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchEmails();
  };

  const handleClear = () => {
    setSearchSender('');
    setSearchSubject('');
    setLimit(50);
    setTimeout(() => fetchEmails(), 100);
  };

  const handleEmailClick = (messageId: string) => {
    navigate(`/emails/${encodeURIComponent(messageId)}`);
  };

  return (
    <div className="email-list-container">
      <div className={`search-section ${isFilterExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="search-header">
          <h2>Email List</h2>
          {isMobile && (
            <button
              type="button"
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
              className="filter-toggle"
              aria-label={isFilterExpanded ? 'Collapse filters' : 'Expand filters'}
            >
              <span className="filter-toggle-icon">
                {isFilterExpanded ? '▼' : '▲'}
              </span>
              <span className="filter-toggle-text">
                {isFilterExpanded ? 'Hide Filters' : 'Show Filters'}
              </span>
            </button>
          )}
        </div>
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-inputs">
            <input
              type="text"
              placeholder="Search by sender..."
              value={searchSender}
              onChange={(e) => setSearchSender(e.target.value)}
              className="search-input"
            />
            <input
              type="text"
              placeholder="Search by subject..."
              value={searchSubject}
              onChange={(e) => setSearchSubject(e.target.value)}
              className="search-input"
            />
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="limit-select"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div className="search-buttons">
            <button type="submit" className="search-button">
              Search
            </button>
            <button type="button" onClick={handleClear} className="clear-button">
              Clear
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading">Loading emails...</div>
      ) : emails.length === 0 ? (
        <div className="empty-state">No emails found</div>
      ) : (
        <div className="email-list">
          {emails.map((email) => (
            <div
              key={email.message_id}
              className="email-item"
              onClick={() => handleEmailClick(email.message_id)}
            >
              <div className="email-header">
                <div className="email-from">{email.from}</div>
                <div className="email-date">
                  {format(new Date(email.timestamp), 'MMM d, yyyy HH:mm')}
                </div>
              </div>
              <div className="email-subject">{email.subject || '(No Subject)'}</div>
              <div className="email-preview">{email.text || '(No content)'}</div>
              <div className="email-badges">
                {email.has_html && <span className="badge badge-html">HTML</span>}
                {email.has_attachments && <span className="badge badge-attachment">Attachment</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmailList;

